//src\components\OrderRequirements\OrderRequirements.jsx
//
import React, { useCallback, useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';

import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';

import {
  fetchOrderRequirementCustomers,
  fetchOrderRequirementQueue,
} from './api/orderRequirementsApi.js';
import CustomerAccordion from './components/CustomerAccordion.jsx';

// Helper function to trim string values
function safeTrim(v) {
  return String(v ?? '').trim();
}

// ============================================================================
// BLOCK ORUI.ADAPT.1 — Queue adapter helper
// ----------------------------------------------------------------------------
// Some environments may return:
//   camelCase: needToOrder / watchList, customerNo / customerName / facilityAddress
// OR
//   snake_case: need_to_order / watch_list, customer_no / customer_name / facility_address
//
// This adapter returns the snake_case shape expected by downstream components.
// It also ensures we only return queue rows for the currently-open customer.
// ============================================================================

function buildQueueAdapter({ isOpen, openCustomerNo, queueState, fallbackCustomer }) {
  if (!isOpen) return null;

  const q = queueState?.data;
  const qCustomerNo = safeTrim(queueState?.customerNo);

  if (!q) return null;
  if (qCustomerNo !== safeTrim(openCustomerNo)) return null;

  const need = Array.isArray(q?.needToOrder)
    ? q.needToOrder
    : Array.isArray(q?.need_to_order)
      ? q.need_to_order
      : [];

  const watch = Array.isArray(q?.watchList)
    ? q.watchList
    : Array.isArray(q?.watch_list)
      ? q.watch_list
      : [];

  return {
    customer_no: q?.customerNo ?? q?.customer_no ?? fallbackCustomer.customerNo,
    customer_name: q?.customerName ?? q?.customer_name ?? fallbackCustomer.customerName,
    facility_address: q?.facilityAddress ?? q?.facility_address ?? fallbackCustomer.facilityAddress,
    need_to_order: need,
    watch_list: watch,
  };
}

// ============================================================================
// BLOCK ORUI.PAGE.1 — getCustomerNo function
// ----------------------------------------------------------------------------
// This function extracts the customer number from the customer object.
// It handles both snake_case and camelCase for consistency across various environments.
// ============================================================================

function getCustomerNo(customer) {
  return safeTrim(customer?.customerNo ?? customer?.customer_no);
}

export default function OrderRequirements() {
  // ============================================================================
  // BLOCK ORUI.STATE.1 — Customers state
  // ============================================================================
  const [customers, setCustomers] = useState({
    pendingCustomers: [],
    noPendingCustomers: [],
  });

  // ============================================================================
  // BLOCK ORUI.STATE.2 — UI selection + queue state (customer-aware)
  // ============================================================================
  const [openCustomerNo, setOpenCustomerNo] = useState('');
  const [queueState, setQueueState] = useState({ customerNo: '', data: null });

  // ============================================================================
  // BLOCK ORUI.STATE.3 — Loading + error flags
  // ============================================================================
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [error, setError] = useState('');
  const [toastState, setToastState] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Used to ignore late async results (fast switching customers)
  const queueRequestSeq = useRef(0);

  const showToast = useCallback((message, severity = 'info') => {
    setToastState({
      open: true,
      message: safeTrim(message),
      severity,
    });
  }, []);

  const closeToast = useCallback(() => {
    setToastState((prev) => ({ ...prev, open: false }));
  }, []);

  // ============================================================================
  // BLOCK ORUI.HANDLERS.0 — Move customer between pending/no-pending sections
  // ============================================================================
  const updateCustomerStatus = useCallback((customerNo, status) => {
    const cleanNo = safeTrim(customerNo);
    if (!cleanNo) return;

    setCustomers((prev) => {
      const pendingCustomers = Array.isArray(prev.pendingCustomers)
        ? [...prev.pendingCustomers]
        : [];
      const noPendingCustomers = Array.isArray(prev.noPendingCustomers)
        ? [...prev.noPendingCustomers]
        : [];

      const takeCustomer = (list) => {
        const idx = list.findIndex((entry) => getCustomerNo(entry) === cleanNo);
        if (idx < 0) return null;
        const [found] = list.splice(idx, 1);
        return found;
      };

      const found = takeCustomer(pendingCustomers) || takeCustomer(noPendingCustomers);
      if (!found) return prev;

      const targetList = status === 'noPending' ? noPendingCustomers : pendingCustomers;
      if (!targetList.some((entry) => getCustomerNo(entry) === cleanNo)) {
        targetList.push(found);
      }

      const sorter = (a, b) => getCustomerNo(a).localeCompare(getCustomerNo(b));
      pendingCustomers.sort(sorter);
      noPendingCustomers.sort(sorter);

      return { pendingCustomers, noPendingCustomers };
    });
  }, []);

  // ============================================================================
  // BLOCK ORUI.LOAD.1 — Load customer list
  // ============================================================================
  const loadCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    setError('');

    try {
      const groups = await fetchOrderRequirementCustomers();

      const pendingCustomers = Array.isArray(groups?.pendingCustomers)
        ? groups.pendingCustomers
        : [];

      const noPendingCustomers = Array.isArray(groups?.noPendingCustomers)
        ? groups.noPendingCustomers
        : [];

      setCustomers({ pendingCustomers, noPendingCustomers });
    } catch (e) {
      setError(e?.message || 'Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // ============================================================================
  // BLOCK ORUI.LOAD.2 — Load queue for a specific customer
  // ============================================================================
  const loadQueueForCustomer = useCallback(async (customerNo) => {
    const cleanNo = safeTrim(customerNo);
    if (!cleanNo) return;

    const seq = queueRequestSeq.current + 1;
    queueRequestSeq.current = seq;

    setLoadingQueue(true);
    setError('');

    try {
      const q = await fetchOrderRequirementQueue(cleanNo);

      if (queueRequestSeq.current !== seq) return;

      setQueueState({ customerNo: cleanNo, data: q });
    } catch (e) {
      if (queueRequestSeq.current !== seq) return;

      setError(e?.message || 'Failed to load queue');
      setQueueState({ customerNo: cleanNo, data: null });
    } finally {
      if (queueRequestSeq.current === seq) setLoadingQueue(false);
    }
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const normalizedSearchTerm = safeTrim(searchTerm).toLowerCase();

  const filterByCustomerNo = useCallback(
    (list) => {
      const source = Array.isArray(list) ? list : [];
      if (!normalizedSearchTerm) return source;

      return source.filter((customer) =>
        getCustomerNo(customer).toLowerCase().includes(normalizedSearchTerm)
      );
    },
    [normalizedSearchTerm]
  );

  const filteredPendingCustomers = filterByCustomerNo(customers.pendingCustomers);
  const filteredNoPendingCustomers = filterByCustomerNo(customers.noPendingCustomers);

  // ============================================================================
  // BLOCK ORUI.EFFECT.1 — Initial load
  // ============================================================================
  useEffect(() => {
    loadCustomers().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });
  }, [loadCustomers]);

  // ============================================================================
  // BLOCK ORUI.HANDLERS.1 — Toggle customer accordion
  // ============================================================================
  const handleToggleCustomer = useCallback(
    async (customerNo) => {
      const cleanNo = safeTrim(customerNo);
      if (!cleanNo) return;

      // Close if already open
      if (cleanNo === openCustomerNo) {
        setOpenCustomerNo('');
        setQueueState({ customerNo: '', data: null });
        return;
      }

      // Open new customer
      setOpenCustomerNo(cleanNo);

      // Load queue for newly opened customer
      await loadQueueForCustomer(cleanNo);
    },
    [openCustomerNo, loadQueueForCustomer]
  );

  // ============================================================================
  // BLOCK ORUI.RENDER.1 — Render customer list
  // ----------------------------------------------------------------------------
  // Display format (HEADER ONLY):
  //   Facility Name — 123 Main St, Portland, OR 97201
  //
  // IMPORTANT:
  // - Read camelCase OR snake_case from customer list API
  // - Force second line off by passing facility_address: ''
  // ============================================================================

  const renderCustomerList = useCallback(
    (list) => {
      if (!Array.isArray(list) || list.length === 0) return null;

      return list.map((c) => {
        const customerNo = getCustomerNo(c);
        const isOpen = customerNo === openCustomerNo;

        const adaptedQueue = buildQueueAdapter({
          isOpen,
          openCustomerNo,
          queueState,
          fallbackCustomer: c,
        });

        const customerForAccordion = {
          customer_no: customerNo,
          customer_name: safeTrim(c.customerName ?? c.customer_name),
          facility_address: safeTrim(c.facilityAddress ?? c.facility_address),
          ship_to_city: safeTrim(c.shipToCity ?? c.ship_to_city),
          ship_to_state: safeTrim(c.shipToState ?? c.ship_to_state),
          ship_to_zip: safeTrim(c.shipToZip ?? c.ship_to_zip),
          ship_to_code: safeTrim(c.shipToCode ?? c.ship_to_code),
          ship_to_addresses: Array.isArray(c.shipToAddresses ?? c.ship_to_addresses)
            ? (c.shipToAddresses ?? c.ship_to_addresses).map((entry) => ({
                facility_address: safeTrim(entry?.facility_address ?? entry?.facilityAddress),
                ship_to_city: safeTrim(entry?.ship_to_city ?? entry?.shipToCity),
                ship_to_state: safeTrim(entry?.ship_to_state ?? entry?.shipToState),
                ship_to_zip: safeTrim(entry?.ship_to_zip ?? entry?.shipToZip),
                ship_to_code: safeTrim(entry?.ship_to_code ?? entry?.shipToCode),
              }))
            : [],
        };

        const name = safeTrim(customerForAccordion.customer_name);
        const street = safeTrim(customerForAccordion.facility_address);
        const city = safeTrim(customerForAccordion.ship_to_city);
        const state = safeTrim(customerForAccordion.ship_to_state);
        const zip = safeTrim(customerForAccordion.ship_to_zip);

        const cityStateZip =
          city || state || zip
            ? `${city}${city && state ? ', ' : ''}${state}${zip ? ` ${zip}` : ''}`
            : '';

        const address = [street, cityStateZip].filter(Boolean).join(', ');

        const facilityLine = name && address ? `${name} — ${address}` : name || address;

        return (
          <CustomerAccordion
            key={customerNo}
            customer={{
              ...customerForAccordion,
              customer_name: facilityLine,
              facility_address: '',
            }}
            queue={adaptedQueue}
            isOpen={isOpen}
            onToggle={() => handleToggleCustomer(customerNo)}
            updateCustomerStatus={updateCustomerStatus}
            notify={showToast}
          />
        );
      });
    },
    [openCustomerNo, queueState, handleToggleCustomer, updateCustomerStatus, showToast]
  );

  // ============================================================================
  // BLOCK ORUI.RENDER.0 — Main render
  // ============================================================================
  return (
    <div style={{ padding: 16 }}>
      {error ? <div style={{ marginBottom: 12 }}>{error}</div> : null}

      {loadingCustomers ? <div>Loading customers...</div> : null}
      {loadingQueue ? <div>Loading queue...</div> : null}

      {/* Search bar added next to "Pending Orders" */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Pending Orders</h3>
        <TextField
          size="small"
          label="Search customer #"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Type customer number"
          InputProps={{
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear customer search"
                  edge="end"
                  onClick={() => setSearchTerm('')}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ marginLeft: 'auto' }}
        />
      </div>

      {filteredPendingCustomers.length === 0 ? (
        <div>No customers with pending orders.</div>
      ) : (
        renderCustomerList(filteredPendingCustomers)
      )}

      {filteredNoPendingCustomers.length > 0 ? (
        <div style={{ marginTop: 28 }}>
          <h3>No Orders Pending</h3>
          {renderCustomerList(filteredNoPendingCustomers)}
        </div>
      ) : null}

      <Snackbar
        open={toastState.open}
        autoHideDuration={3500}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeToast}
          severity={toastState.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
